import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const WishlistContext = createContext();

// Import CartContext to close cart when wishlist opens
// We'll use a ref-based approach to avoid circular dependency
let closeCartRef = null;
export const setCloseCartRef = (fn) => { closeCartRef = fn; };

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function WishlistProvider({ children }) {
  const [wishlist, setWishlist] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isWishlistDrawerOpen, setIsWishlistDrawerOpen] = useState(false);
  const { isAuthenticated, user, token, logout } = useAuth();
  const navigate = useNavigate();
  const hasSyncedRef = useRef(false);
  const hasLoadedRef = useRef(false);

  const fetchWishlist = useCallback(async (attempt = 0) => {
    if (!isAuthenticated || !user || !token) return;
    
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // Allow for backend cold start

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (controller.signal.aborted) return;

      // Handle 401 - session expired
      if (response.status === 401) {
        logout();
        navigate('/login', {
          replace: true,
          state: { reason: 'SESSION_EXPIRED' },
        });
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (controller.signal.aborted) return;

      if (data.success) {
        setWishlist(data.wishlist || []);
        setWishlistCount(data.pagination?.totalCount || data.count || data.wishlist?.length || 0);
        setError(null);
      } else {
        setWishlist([]);
        setWishlistCount(0);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (controller.signal.aborted) {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        // Retry logic: up to 3 attempts with exponential backoff
        const maxRetries = 3;
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          console.warn(`[WishlistContext] Fetch failed, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          
          setTimeout(() => {
            if (!cancelled) {
              fetchWishlist(attempt + 1);
            }
          }, delay);
          return;
        }

        // All retries failed
        setError('Failed to load wishlist. Please refresh the page.');
        if (process.env.NODE_ENV === 'development') {
          console.error('[WishlistContext] fetch error:', err);
        }
        setWishlist([]);
        setWishlistCount(0);
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [isAuthenticated, user, token, navigate, logout, API_URL]);

  const syncGuestWishlist = useCallback(async (products) => {
    if (!isAuthenticated || hasSyncedRef.current) return;
    
    hasSyncedRef.current = true;
    
    try {
      const token = localStorage.getItem('token');
      // Extract valid ObjectIds from products
      const validProductIds = products
        .map(p => p._id || p.id)
        .filter(id => id && (typeof id === 'string' && id.length === 24 && /^[0-9a-fA-F]{24}$/.test(id)));
      
      if (validProductIds.length === 0) {
        // Clear invalid guest wishlist
        localStorage.removeItem('guestWishlist');
        await fetchWishlist();
        return;
      }
      
      // Add all guest wishlist items to server
      await Promise.all(
        validProductIds.map(productId =>
          fetch(`${API_URL}/wishlist/${productId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }).catch(err => {
            console.error(`Error syncing product ${productId}:`, err);
            return null; // Continue with other products
          })
        )
      );
      
      // Clear guest wishlist
      localStorage.removeItem('guestWishlist');
      
      // Fetch updated wishlist
      await fetchWishlist();
    } catch (error) {
      console.error('Error syncing guest wishlist:', error);
      hasSyncedRef.current = false;
    }
  }, [isAuthenticated, fetchWishlist]);

  // Load wishlist from localStorage (guest) or API (logged in)
  useEffect(() => {
    if (isAuthenticated && user) {
      // Reset sync ref when user changes
      if (hasLoadedRef.current && hasLoadedRef.current !== user.id) {
        hasSyncedRef.current = false;
      }
      hasLoadedRef.current = user.id;
      
      // Check for guest wishlist first, then fetch
      const guestWishlist = localStorage.getItem('guestWishlist');
      if (guestWishlist && !hasSyncedRef.current) {
        try {
          const parsed = JSON.parse(guestWishlist);
          if (parsed.length > 0) {
            syncGuestWishlist(parsed);
            return;
          }
        } catch (error) {
          console.error('Error parsing guest wishlist:', error);
        }
      }
      fetchWishlist();
    } else {
      // Guest after sign out or never logged in — show empty or localStorage guest list
      hasLoadedRef.current = 'guest';
      const guestWishlist = localStorage.getItem('guestWishlist');
      if (guestWishlist) {
        try {
          const parsed = JSON.parse(guestWishlist);
          setWishlist(parsed);
          setWishlistCount(parsed.length);
        } catch (error) {
          console.error('Error parsing guest wishlist:', error);
          setWishlist([]);
          setWishlistCount(0);
        }
      } else {
        setWishlist([]);
        setWishlistCount(0);
      }
    }
  }, [isAuthenticated, user?.id, fetchWishlist]);

  const addToWishlist = useCallback(async (productId, productData = null) => {
    // Validate productId is a valid ObjectId format (24 hex characters)
    if (!productId || (typeof productId === 'string' && productId.length !== 24 && !/^[0-9a-fA-F]{24}$/.test(productId))) {
      console.error('Invalid product ID format:', productId);
      return;
    }
    
    // Check if already in wishlist
    if (wishlist.find(item => (item._id || item.id) === productId)) {
      return;
    }
    
    // Optimistic update - use provided productData if available, otherwise minimal object
    const product = productData || { _id: productId, id: productId };
    setWishlist(prev => [...prev, product]);
    setWishlistCount(prev => prev + 1);

    if (isAuthenticated) {
      try {
        // New API: POST /api/wishlist with body { productId, size }
        // Default size to "S" if not provided
        const size = productData?.size || 'S';
        const response = await fetch(`${API_URL}/wishlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            productId: String(productId),
            size: size,
          }),
        });
        
        if (!response.ok) {
          // Revert on error
          setWishlist(prev => prev.filter(item => (item._id || item.id) !== productId));
          setWishlistCount(prev => prev - 1);
        } else {
          // Update count from response
          const data = await response.json();
          if (data.count !== undefined) {
            setWishlistCount(data.count);
          }
        }
        // Don't refresh - optimistic update is enough
      } catch (error) {
        console.error('Error adding to wishlist:', error);
        // Revert on error
        setWishlist(prev => prev.filter(item => (item._id || item.id) !== productId));
        setWishlistCount(prev => prev - 1);
      }
    } else {
      // Store in localStorage for guest
      const guestWishlist = [...wishlist, product];
      localStorage.setItem('guestWishlist', JSON.stringify(guestWishlist));
    }
  }, [isAuthenticated, wishlist]);

  const removeFromWishlist = useCallback(async (productId) => {
    // Store previous state for potential revert
    const previousWishlist = [...wishlist];
    const previousCount = wishlistCount;
    
    // Optimistic update
    setWishlist(prev => prev.filter(item => (item._id || item.id) !== productId));
    setWishlistCount(prev => Math.max(0, prev - 1));

    if (isAuthenticated) {
      try {
        const response = await fetch(`${API_URL}/wishlist/${productId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          // Revert on error
          setWishlist(previousWishlist);
          setWishlistCount(previousCount);
        }
        // Don't refresh - optimistic update is enough
      } catch (error) {
        console.error('Error removing from wishlist:', error);
        // Revert on error
        setWishlist(previousWishlist);
        setWishlistCount(previousCount);
      }
    } else {
      // Update localStorage for guest
      const guestWishlist = wishlist.filter(item => (item._id || item.id) !== productId);
      localStorage.setItem('guestWishlist', JSON.stringify(guestWishlist));
    }
  }, [isAuthenticated, wishlist, wishlistCount]);

  const isInWishlist = useCallback((productId) => {
    return wishlist.some(item => (item._id || item.id) === productId);
  }, [wishlist]);

  const cleanupAfterPurchase = async (productIds) => {
    if (!isAuthenticated) return;
    
    try {
      await fetch(`${API_URL}/wishlist/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ productIds }),
      });
      
      // Refresh wishlist
      fetchWishlist();
    } catch (error) {
      console.error('Error cleaning up wishlist:', error);
    }
  };

  const openWishlistDrawer = useCallback(() => {
    // Close cart drawer if it's open
    if (closeCartRef) {
      closeCartRef();
    }
    setIsWishlistDrawerOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeWishlistDrawer = useCallback(() => {
    setIsWishlistDrawerOpen(false);
    document.body.style.overflow = '';
  }, []);

  const value = {
    wishlist,
    wishlistCount,
    loading,
    error,
    isWishlistDrawerOpen,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    cleanupAfterPurchase,
    refreshWishlist: fetchWishlist,
    openWishlistDrawer,
    closeWishlistDrawer,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
}
